import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, distinctUntilChanged, take } from 'rxjs/operators';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import { apiParameters } from '../../configs/api.params';
import { IUserModel, IUser } from '../models/user.model';
import { UserService } from './user.service';
import { CustomerService } from './customer.service';
import { Role } from '../models/role';
import { StorageService } from './storage/storage.service';
const userKey = 'currentUser';
const tokenKey = 'token';
const rememberMeKey = 'rememberMe';

@Injectable({ providedIn: 'root' })
export class AuthService {
  INIT_VALUE = null;

  private isAuthenticated$ = new BehaviorSubject<boolean>(this.INIT_VALUE);
  public isAuthenticated = this.isAuthenticated$.asObservable();

  private LoggedinUser$ = new BehaviorSubject<IUser>(this.INIT_VALUE);
  public LoggedinUser = this.LoggedinUser$.asObservable().pipe(
    distinctUntilChanged()
  );

  private userToken$ = new BehaviorSubject<string>(this.INIT_VALUE);
  public userToken = this.userToken$
    .asObservable()
    .pipe(distinctUntilChanged());

  private rememberMe$ = new BehaviorSubject<boolean>(false);
  private rememberMe = this.rememberMe$.value;

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private customerService: CustomerService,
    private storage: StorageService,
  ) {}

  /**
   * if we have token the user is loggedIn
   * @returns {boolean}
   */
  private hasToken(): boolean {
    return !!sessionStorage.getItem('token');
  }

  /**
   * Gets token if it exits in the seession storage if not get it from local storage
   * @returns
   */
  public getToken() {
    const token =
      sessionStorage.getItem('token') || localStorage.getItem('token');
    return of(token);
  }

  public get currentUserValue(): any {
    return this.LoggedinUser$.value;
  }

  public setUser(user: IUser) {
    const token = this.userToken$.value;
    const rememberMe = this.rememberMe$.value;

    this.LoggedinUser$.next(user);

    rememberMe
      ? this.storage.saveUser(localStorage, JSON.stringify({ user, token }))
      : this.storage.saveUser(sessionStorage, JSON.stringify({ user, token }));
  }

  /**
   *
   * Login the user then tell all the subscribers about the new status
   * @param email - user email
   * @param password - user password
   * @param remember - is true if the user clicked remember me
   * @returns
   */
  login(email: string, password: string, remember: boolean) {
    return this.http
      .post(
        apiParameters.url + '/login',
        {
          email,
          password,
        },
        {
          headers: new HttpHeaders().set('Content-Type', 'application/json'),
        }
      )
      .pipe(
        tap((res: IUserModel) => {
          const token = res.token;
          const user = res.user;
          if (token) {
            const storage = remember ? localStorage : sessionStorage;

            this.storage.saveUser(storage, JSON.stringify({ user, token }));
            this.storage.saveToken(storage, token);

            if (remember) {
              this.storage.setLocalObject(
                rememberMeKey,
                JSON.stringify(remember)
              );
              this.rememberMe$.next(true);
            }

            this.userToken$.next(token);
            this.LoggedinUser$.next(user);
            this.isAuthenticated$.next(true);

          }
        })
      );
  }

  /**
   * Login the user as root
   * @param userId - user id
   * @returns logged in user
   */
  loginUserAsRoot(userId: string): Observable<IUserModel> {
    return this.http.post<IUserModel>(
      `${apiParameters.url}/users/${userId}/login`,
      {}
    );
  }

  /**
   *
   * Log out the user then tell all the subscribers about the new status
   */
  logout(): void {
    this.storage.removeSessionObject(userKey);
    this.storage.removeSessionObject(tokenKey);
    this.storage.removeSessionObject('user');
    this.storage.removeSessionObject('userToken');
    this.storage.removeLocalObject(userKey);
    this.storage.removeLocalObject(tokenKey);
    this.storage.removeLocalObject(rememberMeKey);

    this.isAuthenticated$.next(false);
    this.LoggedinUser$.next(null);
    this.userToken$.next(null);
    this.rememberMe$.next(false);
  }

  /**
   * Auto Login user - Check if user is in session storage, if so log in user
   * @returns
   */
  autoLogin() {
    let rememberMe;
    if (this.storage.getLocalObject(rememberMeKey)) {
      this.rememberMe$.next(this.storage.getLocalObject(rememberMeKey));
      rememberMe = this.rememberMe$.value;
    }

    const userData: IUserModel = rememberMe
      ? this.storage.getLocalObject(userKey)
      : this.storage.getSessionObject(userKey);

    const userAsRoot: IUserModel = this.storage.getSessionObject('user');

    if (!userData && !userAsRoot) {
      this.isAuthenticated$.next(false);
      return;
    }

    const user = userAsRoot || userData;

    this.userToken$.next(user.token);
    // this.LoggedinUser$.next(userData.user);
    // this.isAuthenticated$.next(true);

    if (user.user.role.name !== Role.CUSTOMER) {
      return this.userService
        .getById(user.user.id)
        .pipe(take(1))
        .subscribe(
          (user) => {
            this.LoggedinUser$.next(user);
            this.isAuthenticated$.next(true);
            // sessionStorage.setItem('currentUser', JSON.stringify({ user, token }));
          },
          (err) => {
            this.logout();
            // this.router.navigate(['/login']);
          }
        );
    } else {
      return this.customerService
        .getCustomerAdmin(user.user.customerId, user.user.id)
        .pipe(take(1))
        .subscribe(
          (user) => {
            this.LoggedinUser$.next(user);
            this.isAuthenticated$.next(true);
            // sessionStorage.setItem('currentUser', JSON.stringify({ user, token }));
          },
          (err) => {
            this.logout();
            // this.router.navigate(['/login']);
          }
        );
    }

    // const loadedUser = new User(
    //   userData.email,
    //   userData.id,
    //   userData._token,
    //   new Date(userData._tokenExpirationDate)
    // );

    // if (loadedUser.token) {
    //   this.user.next(loadedUser);
    //   const expirationDuration =
    //     new Date(userData._tokenExpirationDate).getTime() -
    //     new Date().getTime();
    //   this.autoLogout(expirationDuration);
    // }
  }

  forgotPassword(email: string): Observable<any> {
    let body = {
      email: email,
    };
    return this.http.post(`${apiParameters.url}/password/forgot`, body);
  }

  resetPassword(token: string, password: string): Observable<any> {
    let body = {
      resetToken: token,
      password: password,
    };
    return this.http.post(`${apiParameters.url}/password/reset`, body);
  }

  /**
   * Send Instagram authorization code and optional state to backend to get access token
   * @param code - Instagram authorization code
   * @param state - Optional state parameter
   * @returns Observable
   */
  exchangeInstagramCodeForToken(code: string, state?: string): Observable<any> {
    const body: any = { code };

    if (state) {
      body.state = state;
    }

    return this.http.post(`${apiParameters.url}/auth/instagram/callback`, body);
  }

  getOauthTokens(): Observable<any> {
    return this.http.get(`${apiParameters.urlV2}/oauth-tokens`);
  }

  deleteOauthToken(tokenId: string): Observable<any> {
    return this.http.delete(`${apiParameters.urlV2}/oauth-tokens/${tokenId}`);
  }

  getUserOauthRedirect(dmsId: string, userId: string, profileId?: string): Observable<any> {
    let httpOptions = {};
  
    let params = new HttpParams();
    if (profileId) {
      params = params.append('profileId', profileId);
    }

    httpOptions = { params };
  
    return this.http.get(
      `${apiParameters.urlV2}/users/${userId}/dms/${dmsId}/oauth-redirect`, 
      httpOptions
    );
  }

  getClientOauthRedirect(clientId: string, dmsId: string): Observable<any> {
    return this.http.get(
      `${apiParameters.urlV2}/clients/${clientId}/dms/${dmsId}/oauth-redirect`
    );
  }
}
